import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AppSettings, WorkOrderFormData, SavedWorkOrder, TravelItem, WorkItem, PartItem, CustomLineItem } from '../types';
import {
  generateWorkOrderNumber,
  generateJobNumber,
  saveWorkOrder,
  updateWorkOrder,
  loadWorkOrders,
  loadOffers,
  calculateTotals,
  formatCurrency
} from '../utils';
import { Confetti, useConfetti } from './Confetti';

// Session storage key for work order form persistence
const WORKORDER_SESSION_KEY = 'halton_workorder_form_session';

interface WorkOrderFormProps {
  settings: AppSettings;
  editingOfferId: string | null;
  editingWorkOrderId: string | null;
  setEditingWorkOrderId: (id: string | null) => void;
  onSaved: () => void;
  onNavigateToCalculator?: () => void; // Optional - kept for backward compatibility but not used
  onPrint?: (workOrder: SavedWorkOrder, source: 'form' | 'archive') => void;
  onCreateWorkOrderFromOffer?: (offer: any) => void;
  initialFormData?: Partial<WorkOrderFormData>;
  pricingItems?: {
    travelItems: TravelItem[];
    workItems: WorkItem[];
    parts: PartItem[];
    customLines: CustomLineItem[];
  };
  // New: callback to update pricing items in parent (App.tsx)
  onPricingItemsChange?: (items: {
    travelItems: TravelItem[];
    workItems: WorkItem[];
    parts: PartItem[];
    customLines: CustomLineItem[];
  }) => void;
}

const initialFormData: WorkOrderFormData = {
  yhteyshenkilo: '',
  sahkoposti: '',
  puhelin: '',
  yritys: '',
  asiakasnumero: '',
  ytunnus: '',
  osoite: '',
  postinumero: '',
  paikkakunta: '',
  ovt: '',
  operaattori: '',
  viite: '',
  // New billing fields
  tilaaja: '',
  laskutusosoite: '',
  verkkolaskuosoite: '',
  verkkolaskuoperaattori: '',
  // Order details
  tilausnumero: '',
  jobNumber: '',
  ourReference: '',
  kohde: '',
  kohdeOsoite: '',
  kohdePostinumero: '',
  kohdePaikkakunta: '',
  ajankohta: '',
  kuvaus: '',
  merkki: '',
  lineOfBusiness: '',
  tuoteryhma: '',
  lisatietoja: '',
  teknikonNimi: '',
  paivamaara: new Date().toISOString().split('T')[0],
};

const WorkOrderForm = ({
  settings,
  editingOfferId,
  editingWorkOrderId,
  setEditingWorkOrderId,
  onPrint,
  onSaved,
  onCreateWorkOrderFromOffer,
  initialFormData: propInitialFormData,
  pricingItems,
  onPricingItemsChange,
}: WorkOrderFormProps) => {
  const { t } = useTranslation();
  const { showConfetti, triggerConfetti, handleComplete } = useConfetti();
  
  // Load initial form data from sessionStorage if available
  const getInitialFormState = () => {
    // Don't load from session if we're editing an existing work order or have initial data from offer
    if (editingWorkOrderId || propInitialFormData) {
      return initialFormData;
    }
    const saved = sessionStorage.getItem(WORKORDER_SESSION_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return { ...initialFormData, ...parsed.formData };
      } catch (e) {
        console.error('Failed to parse saved work order form:', e);
      }
    }
    return initialFormData;
  };

  const getInitialPricingState = () => {
    // Don't load from session if we're editing an existing work order or have pricing from offer
    if (editingWorkOrderId || pricingItems) {
      return { travelItems: [], workItems: [], parts: [], customLines: [] };
    }
    const saved = sessionStorage.getItem(WORKORDER_SESSION_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          travelItems: parsed.travelItems || [],
          workItems: parsed.workItems || [],
          parts: parsed.parts || [],
          customLines: parsed.customLines || [],
        };
      } catch (e) {
        console.error('Failed to parse saved work order pricing:', e);
      }
    }
    return { travelItems: [], workItems: [], parts: [], customLines: [] };
  };

  const initialPricing = getInitialPricingState();
  const [formData, setFormData] = useState<WorkOrderFormData>(getInitialFormState);
  const [linkedOfferId, setLinkedOfferId] = useState<string | undefined>(undefined);
  
  // State for collapsible sections
  const [isShippingExpanded, setIsShippingExpanded] = useState(false);
  const [isEInvoicingExpanded, setIsEInvoicingExpanded] = useState(false);
  
  // State for job number validation
  const [jobNumberError, setJobNumberError] = useState<string | null>(null);

  // State for advisory form validation
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof WorkOrderFormData, string>>>({});
  
  // NEW: State for editing pricing inline
  const [isEditingPricing, setIsEditingPricing] = useState(false);
  const [localTravelItems, setLocalTravelItems] = useState<TravelItem[]>(pricingItems?.travelItems || initialPricing.travelItems);
  const [localWorkItems, setLocalWorkItems] = useState<WorkItem[]>(pricingItems?.workItems || initialPricing.workItems);
  const [localParts, setLocalParts] = useState<PartItem[]>(pricingItems?.parts || initialPricing.parts);
  const [localCustomLines, setLocalCustomLines] = useState<CustomLineItem[]>(pricingItems?.customLines || initialPricing.customLines);

  // Initialize local pricing items from props
  useEffect(() => {
    setLocalTravelItems(pricingItems?.travelItems || []);
    setLocalWorkItems(pricingItems?.workItems || []);
    setLocalParts(pricingItems?.parts || []);
    setLocalCustomLines(pricingItems?.customLines || []);
  }, [pricingItems]);

  // Save work order form to sessionStorage whenever it changes (only when not editing existing)
  useEffect(() => {
    // Don't save to session if editing an existing work order
    if (editingWorkOrderId) return;
    
    const sessionState = {
      formData,
      travelItems: localTravelItems,
      workItems: localWorkItems,
      parts: localParts,
      customLines: localCustomLines,
    };
    sessionStorage.setItem(WORKORDER_SESSION_KEY, JSON.stringify(sessionState));
  }, [formData, localTravelItems, localWorkItems, localParts, localCustomLines, editingWorkOrderId]);

  // Calculate totals from local pricing items (when editing) or props (when not)
  const calculatedTotals = useMemo(() => {
    if (isEditingPricing) {
      return calculateTotals(
        localTravelItems,
        localWorkItems,
        localParts,
        localCustomLines,
        settings
      );
    }
    return calculateTotals(
      pricingItems?.travelItems || [],
      pricingItems?.workItems || [],
      pricingItems?.parts || [],
      pricingItems?.customLines || [],
      settings
    );
  }, [isEditingPricing, localTravelItems, localWorkItems, localParts, localCustomLines, pricingItems, settings]);

  // === PRICING ITEM HANDLERS ===
  
  // Travel Items
  const addTravelItem = () => {
    const newItem: TravelItem = {
      id: Date.now(),
      mode: 'perKm',
      distance: 0,
      travelTime: 0,
      timeRate: settings.defaultTimeRate,
      distanceRate: settings.defaultDistanceRate,
      clientsOnTrip: 1,
      discountPercent: 0,
      fixedPrice: 0,
      tripMultiplier: 1,
    };
    setLocalTravelItems([...localTravelItems, newItem]);
  };

  const updateTravelItem = (id: number, field: keyof TravelItem, value: number | string) => {
    setLocalTravelItems(localTravelItems.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const removeTravelItem = (id: number) => {
    setLocalTravelItems(localTravelItems.filter(item => item.id !== id));
  };

  // Work Items
  const addWorkItem = () => {
    const newItem: WorkItem = {
      id: Date.now(),
      technicians: 1,
      hours: 0,
      hourlyRate: settings.defaultHourlyRate,
      callOutFee: 0,
      customCallOutFee: 0,
      nightSunday: false,
      urgent: false,
      accommodationNights: 0,
      perDiemDays: 0,
    };
    setLocalWorkItems([...localWorkItems, newItem]);
  };

  const updateWorkItem = (id: number, field: keyof WorkItem, value: number | boolean) => {
    setLocalWorkItems(localWorkItems.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const removeWorkItem = (id: number) => {
    setLocalWorkItems(localWorkItems.filter(item => item.id !== id));
  };

  // Parts
  const addPart = () => {
    const newPart: PartItem = {
      id: Date.now(),
      description: '',
      quantity: 1,
      unitPrice: 0,
      markup: 15,
    };
    setLocalParts([...localParts, newPart]);
  };

  const updatePart = (id: number, field: keyof PartItem, value: string | number) => {
    setLocalParts(localParts.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const removePart = (id: number) => {
    setLocalParts(localParts.filter(item => item.id !== id));
  };

  // Custom Lines
  const addCustomLine = () => {
    const newLine: CustomLineItem = {
      id: Date.now(),
      name: '',
      description: '',
      price: 0,
    };
    setLocalCustomLines([...localCustomLines, newLine]);
  };

  const updateCustomLine = (id: number, field: keyof CustomLineItem, value: string | number) => {
    setLocalCustomLines(localCustomLines.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const removeCustomLine = (id: number) => {
    setLocalCustomLines(localCustomLines.filter(item => item.id !== id));
  };

  // Toggle pricing edit mode
  const handleTogglePricingEdit = () => {
    if (!isEditingPricing) {
      // Entering edit mode - initialize from props
      setLocalTravelItems(pricingItems?.travelItems || []);
      setLocalWorkItems(pricingItems?.workItems || []);
      setLocalParts(pricingItems?.parts || []);
      setLocalCustomLines(pricingItems?.customLines || []);
    }
    setIsEditingPricing(!isEditingPricing);
  };

  // Apply pricing changes (update parent state)
  const handleApplyPricingChanges = () => {
    if (onPricingItemsChange) {
      onPricingItemsChange({
        travelItems: localTravelItems,
        workItems: localWorkItems,
        parts: localParts,
        customLines: localCustomLines,
      });
    }
    setIsEditingPricing(false);
  };

  // Cancel pricing edit (revert to props)
  const handleCancelPricingEdit = () => {
    setLocalTravelItems(pricingItems?.travelItems || []);
    setLocalWorkItems(pricingItems?.workItems || []);
    setLocalParts(pricingItems?.parts || []);
    setLocalCustomLines(pricingItems?.customLines || []);
    setIsEditingPricing(false);
  };

  // Load existing work order if editing, or merge with initial data from offer
  useEffect(() => {
    if (editingWorkOrderId) {
      const workOrders = loadWorkOrders();
      const existing = workOrders.find(wo => wo.id === editingWorkOrderId);
      if (existing) {
        // Persisted formData may be partial/missing keys (legacy data). Always merge with full defaults
        // to avoid controlled/uncontrolled warnings and runtime crashes.
        setFormData({ ...initialFormData, ...existing.formData });
        setLinkedOfferId(existing.linkedOfferId);
      }
    } else if (propInitialFormData) {
      // Merge initial data from offer with default form data
      // This includes jobNumber when creating work order from quote
      setFormData({ ...initialFormData, ...propInitialFormData });
      setLinkedOfferId(undefined);
    } else {
      setFormData(initialFormData);
      setLinkedOfferId(undefined);
    }
  }, [editingWorkOrderId, propInitialFormData]);

  // Clear job number error when job number changes
  useEffect(() => {
    if (formData.jobNumber) {
      setJobNumberError(null);
    }
  }, [formData.jobNumber]);

  // Generate job number handler
  const handleGenerateJobNumber = () => {
    const newJobNumber = generateJobNumber(settings.userIdentityNumber);
    setFormData(prev => ({ ...prev, jobNumber: newJobNumber }));
  };

  // Validate job number before save/print
  const validateJobNumber = (): boolean => {
    const job = (formData.jobNumber ?? '').trim();
    if (!job) {
      setJobNumberError(t('workOrder.jobNumberRequired'));
      return false;
    }
    setJobNumberError(null);
    return true;
  };

  const validateFormForSave = (): boolean => {
    const errors: Partial<Record<keyof WorkOrderFormData, string>> = {};
    const requiredFields: Array<keyof WorkOrderFormData> = [
      'yritys',
      'asiakasnumero',
      'viite',
      'yhteyshenkilo',
      'sahkoposti',
      'puhelin',
      'tilausnumero',
      'kohde',
      'ajankohta',
    ];

    requiredFields.forEach((field) => {
      const value = (formData[field] ?? '').toString().trim();
      if (!value) {
        errors[field] = t('validation.required');
      }
    });

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (field: keyof WorkOrderFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const { [field]: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  // Get the current pricing items (local if editing, otherwise from props)
  const getCurrentPricingItems = () => ({
    travelItems: isEditingPricing ? localTravelItems : (pricingItems?.travelItems || []),
    workItems: isEditingPricing ? localWorkItems : (pricingItems?.workItems || []),
    parts: isEditingPricing ? localParts : (pricingItems?.parts || []),
    customLines: isEditingPricing ? localCustomLines : (pricingItems?.customLines || []),
  });

  const handleSave = () => {
    // Hard requirement: job number must be present (no confirm override)
    if (!validateJobNumber()) {
      return;
    }

    // Advisory validation: warn but allow save with confirmation
    const isValid = validateFormForSave();
    if (!isValid) {
      const confirmMessage = t('validation.confirmProceedWithWarnings');
      if (!window.confirm(confirmMessage)) {
        return;
      }
    }

    const currentPricing = getCurrentPricingItems();
    
    if (editingWorkOrderId) {
      // Update existing work order
      const workOrders = loadWorkOrders();
      const existing = workOrders.find(wo => wo.id === editingWorkOrderId);
      if (existing) {
        updateWorkOrder({
          ...existing,
          formData,
          linkedOfferId,
          // Include updated pricing items
          travelItems: currentPricing.travelItems,
          workItems: currentPricing.workItems,
          parts: currentPricing.parts,
          customLines: currentPricing.customLines,
          totals: calculateTotals(
            currentPricing.travelItems,
            currentPricing.workItems,
            currentPricing.parts,
            currentPricing.customLines,
            settings
          ),
          updatedAt: new Date().toISOString(),
        });
      }
      
      // If we were editing pricing, apply changes to parent
      if (isEditingPricing && onPricingItemsChange) {
        onPricingItemsChange(currentPricing);
      }

      onSaved();
      setIsEditingPricing(false);
      triggerConfetti();
      // Don't clear form - user can continue editing
    } else {
      // Create new work order
      const newWorkOrderId = crypto.randomUUID();
      const newWorkOrder: SavedWorkOrder = {
        id: newWorkOrderId,
        documentNumber: generateWorkOrderNumber(),
        formData,
        linkedOfferId,
        travelItems: currentPricing.travelItems,
        workItems: currentPricing.workItems,
        parts: currentPricing.parts,
        customLines: currentPricing.customLines,
        totals: calculateTotals(
          currentPricing.travelItems,
          currentPricing.workItems,
          currentPricing.parts,
          currentPricing.customLines,
          settings
        ),
        settings: {
          printLanguage: settings.printLanguage,
          companyCountry: settings.companyCountry,
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      saveWorkOrder(newWorkOrder);
      
      // Set the new work order as being edited so subsequent saves update it
      setEditingWorkOrderId(newWorkOrderId);
      
      // If we were editing pricing, apply changes to parent
      if (isEditingPricing && onPricingItemsChange) {
        onPricingItemsChange(currentPricing);
      }

      onSaved();
      setIsEditingPricing(false);
      triggerConfetti();
      // Don't clear form - user can continue editing the now-saved work order
    }
  };

  const handlePrint = () => {
    // Validate job number before printing
    if (!validateJobNumber()) {
      return;
    }

    // Advisory validation: warn but allow print with confirmation
    const isValid = validateFormForSave();
    if (!isValid) {
      const confirmMessage = t('validation.confirmProceedWithWarnings');
      if (!window.confirm(confirmMessage)) {
        return;
      }
    }

    const currentPricing = getCurrentPricingItems();
    
    if (editingWorkOrderId) {
      const allWorkOrders = loadWorkOrders();
      const currentWorkOrder = allWorkOrders.find(wo => wo.id === editingWorkOrderId);
      if (currentWorkOrder && onPrint) {
        // Print with current (possibly edited) pricing
        onPrint({
          ...currentWorkOrder,
          formData, // Use current form data, not saved data
          travelItems: currentPricing.travelItems,
          workItems: currentPricing.workItems,
          parts: currentPricing.parts,
          customLines: currentPricing.customLines,
          totals: calculatedTotals,
        }, 'form');
      }
    } else if (onPrint) {
      // Create temporary work order for printing
      const tempWorkOrder: SavedWorkOrder = {
        id: crypto.randomUUID(),
        documentNumber: generateWorkOrderNumber(),
        formData,
        linkedOfferId,
        travelItems: currentPricing.travelItems,
        workItems: currentPricing.workItems,
        parts: currentPricing.parts,
        customLines: currentPricing.customLines,
        totals: calculatedTotals,
        settings: {
          printLanguage: settings.printLanguage,
          companyCountry: settings.companyCountry,
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      onPrint(tempWorkOrder, 'form');
    }
  };

  const handleClear = () => {
    // Show confirmation dialog before clearing
    const confirmMessage = t('workOrder.actions.clearFormConfirm') || 'Are you sure you want to clear the form? All unsaved data will be lost.';
    if (!window.confirm(confirmMessage)) {
      return;
    }
    // Clear sessionStorage when explicitly clearing the form
    sessionStorage.removeItem(WORKORDER_SESSION_KEY);
    setFormData(initialFormData);
    setLinkedOfferId(undefined);
    setFieldErrors({});
    setJobNumberError(null);
    setEditingWorkOrderId(null);
    setIsEditingPricing(false);
    setLocalTravelItems([]);
    setLocalWorkItems([]);
    setLocalParts([]);
    setLocalCustomLines([]);
  };

  return (
    <div className="workorder-page">
      <Confetti active={showConfetti} onComplete={handleComplete} />
      {/* Banner when editing an existing work order */}
      {editingWorkOrderId && (() => {
        const workOrders = loadWorkOrders();
        const workOrder = workOrders.find(wo => wo.id === editingWorkOrderId);
        return workOrder ? (
          <div className="edit-banner">
            <span className="edit-banner-icon">✏️</span>
            <span className="edit-banner-text">
              {t('workOrder.editingWorkOrder') || 'Editing Work Order'}: <strong>{workOrder.documentNumber}</strong>
              {workOrder.formData.yritys && ` - ${workOrder.formData.yritys} `}
            </span>
          </div>
        ) : null;
      })()}

      {/* Banner when an offer is being edited on Calculator page */}
      {!editingWorkOrderId && editingOfferId && (() => {
        const offers = loadOffers();
        const offer = offers.find(o => o.id === editingOfferId);
        return offer ? (
          <div className="edit-banner edit-banner-info">
            <span className="edit-banner-icon">📊</span>
            <div className="edit-banner-content">
              <span className="edit-banner-text">
                {t('workOrder.offerInEditMode') || 'Offer is being edited'}: <strong>{offer.documentNumber}</strong>
                {offer.customerName && ` - ${offer.customerName} `}
              </span>
              <button
                className="btn btn-sm btn-white"
                onClick={() => onCreateWorkOrderFromOffer && onCreateWorkOrderFromOffer(offer)}
              >
                {t('workOrder.convertOfferToWorkOrder') || 'Convert to Work Order'}
              </button>
            </div>
          </div>
        ) : null;
      })()}

      <div className="page-header">
        <h1>{t('workOrder.title')}</h1>
      </div>

      <div className="calculator-layout">
        {/* Left Column - Form Content */}
        <div className="calculator-content">
          {/* Job Number Section - PROMINENT at top */}
          <section className="form-section form-section-prominent">
            <h2 className="section-title">
              🔢 {t('workOrder.jobNumber')} <span style={{ color: 'var(--color-error, #dc2626)' }}>*</span>
            </h2>
            <div className="btn-group" style={{ alignItems: 'flex-start' }}>
              <div className="form-group" style={{ flex: 1, maxWidth: '200px' }}>
                <input
                  type="text"
                  value={formData.jobNumber}
                  onChange={(e) => handleChange('jobNumber', e.target.value)}
                  placeholder={t('workOrder.jobNumberPlaceholder')}
                  className={`form-input input-job-number ${jobNumberError ? 'input-error' : ''}`}
                />
                {jobNumberError && (
                  <span className="error-message">
                    ⚠️ {jobNumberError}
                  </span>
                )}
              </div>
              <button 
                className="btn btn-secondary"
                onClick={handleGenerateJobNumber}
                type="button"
                style={{ height: '42px' }}
              >
                {t('workOrder.generateJobNumber')}
              </button>
            </div>
          </section>

          {/* Billing Information */}
          <section className="form-section">
            <h2 className="section-title">{t('workOrder.billing.title')} <span style={{ color: 'var(--color-error, #dc2626)' }}>*</span></h2>

            <div className="form-row form-row-3">
              <div className="form-group">
                <label>{t('workOrder.billing.company')} <span style={{ color: 'var(--color-error, #dc2626)' }}>*</span></label>
                <input
                  type="text"
                  value={formData.yritys}
                  onChange={(e) => handleChange('yritys', e.target.value)}
                  className={`form-input ${fieldErrors.yritys ? 'input-error' : ''}`}
                />
                {fieldErrors.yritys && (
                  <span className="error-message">⚠️ {fieldErrors.yritys}</span>
                )}
              </div>

              <div className="form-group">
                <label>{t('workOrder.billing.customerNumber')} <span style={{ color: 'var(--color-error, #dc2626)' }}>*</span></label>
                <input
                  type="text"
                  value={formData.asiakasnumero}
                  onChange={(e) => handleChange('asiakasnumero', e.target.value)}
                  className={`form-input ${fieldErrors.asiakasnumero ? 'input-error' : ''}`}
                />
                {fieldErrors.asiakasnumero && (
                  <span className="error-message">⚠️ {fieldErrors.asiakasnumero}</span>
                )}
              </div>

              <div className="form-group">
                <label>{t('workOrder.billing.vatId')}</label>
                <input
                  type="text"
                  value={formData.ytunnus}
                  onChange={(e) => handleChange('ytunnus', e.target.value)}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-row form-row-2">
              <div className="form-group">
                <label>{t('workOrder.billing.orderer')}</label>
                <input
                  type="text"
                  value={formData.tilaaja}
                  onChange={(e) => handleChange('tilaaja', e.target.value)}
                  placeholder={t('workOrder.billing.ordererPlaceholder')}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>{t('workOrder.billing.billingAddress')}</label>
                <input
                  type="text"
                  value={formData.laskutusosoite}
                  onChange={(e) => handleChange('laskutusosoite', e.target.value)}
                  placeholder={t('workOrder.billing.billingAddressPlaceholder')}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label>{t('workOrder.billing.customerReference')} <span style={{ color: 'var(--color-error, #dc2626)' }}>*</span></label>
              <input
                type="text"
                value={formData.viite}
                onChange={(e) => handleChange('viite', e.target.value)}
                className={`form-input ${fieldErrors.viite ? 'input-error' : ''}`}
              />
              {fieldErrors.viite && (
                <span className="error-message">⚠️ {fieldErrors.viite}</span>
              )}
              <small className="form-hint">{t('workOrder.billing.customerReferenceHint')}</small>
            </div>

            {/* Electronic Invoicing - Collapsible */}
            <div className="form-subsection">
              <h3 
                className="subsection-title" 
                onClick={() => setIsEInvoicingExpanded(!isEInvoicingExpanded)}
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.95rem' }}
              >
                <span style={{ fontSize: '0.75em', transition: 'transform 0.2s' }}>
                  {isEInvoicingExpanded ? '▼' : '▶'}
                </span>
                {t('workOrder.billing.eInvoicing')}
              </h3>

              {isEInvoicingExpanded && (
                <div className="form-row form-row-2" style={{ marginTop: '12px' }}>
                  <div className="form-group">
                    <label>{t('workOrder.billing.eInvoiceAddress')}</label>
                    <input
                      type="text"
                      value={formData.verkkolaskuosoite}
                      onChange={(e) => handleChange('verkkolaskuosoite', e.target.value)}
                      placeholder={t('workOrder.billing.eInvoiceAddressPlaceholder')}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('workOrder.billing.eInvoiceOperator')}</label>
                    <input
                      type="text"
                      value={formData.verkkolaskuoperaattori}
                      onChange={(e) => handleChange('verkkolaskuoperaattori', e.target.value)}
                      placeholder={t('workOrder.billing.eInvoiceOperatorPlaceholder')}
                      className="form-input"
                    />
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Contact Person / Reference */}
          <section className="form-section">
            <h2 className="section-title">{t('workOrder.contact.title')} <span style={{ color: 'var(--color-error, #dc2626)' }}>*</span></h2>

            <div className="form-row form-row-3">
              <div className="form-group">
                <label>{t('workOrder.order.contactPerson')}</label>
                <input
                  type="text"
                  value={formData.yhteyshenkilo}
                  onChange={(e) => handleChange('yhteyshenkilo', e.target.value)}
                  className={`form-input ${fieldErrors.yhteyshenkilo ? 'input-error' : ''}`}
                />
                {fieldErrors.yhteyshenkilo && (
                  <span className="error-message">⚠️ {fieldErrors.yhteyshenkilo}</span>
                )}
              </div>

              <div className="form-group">
                <label>{t('workOrder.order.email')}</label>
                <input
                  type="email"
                  value={formData.sahkoposti}
                  onChange={(e) => handleChange('sahkoposti', e.target.value)}
                  className={`form-input ${fieldErrors.sahkoposti ? 'input-error' : ''}`}
                />
                {fieldErrors.sahkoposti && (
                  <span className="error-message">⚠️ {fieldErrors.sahkoposti}</span>
                )}
              </div>

              <div className="form-group">
                <label>{t('workOrder.order.phone')}</label>
                <input
                  type="tel"
                  value={formData.puhelin}
                  onChange={(e) => handleChange('puhelin', e.target.value)}
                  className={`form-input ${fieldErrors.puhelin ? 'input-error' : ''}`}
                />
                {fieldErrors.puhelin && (
                  <span className="error-message">⚠️ {fieldErrors.puhelin}</span>
                )}
              </div>
            </div>

            <div className="form-row form-row-2">
              <div className="form-group">
                <label>{t('workOrder.order.orderNumber')}</label>
                <input
                  type="text"
                  value={formData.tilausnumero}
                  onChange={(e) => handleChange('tilausnumero', e.target.value)}
                  className={`form-input ${fieldErrors.tilausnumero ? 'input-error' : ''}`}
                />
                {fieldErrors.tilausnumero && (
                  <span className="error-message">⚠️ {fieldErrors.tilausnumero}</span>
                )}
              </div>

              <div className="form-group">
                <label>{t('workOrder.order.ourReference')}</label>
                <input
                  type="text"
                  value={formData.ourReference}
                  onChange={(e) => handleChange('ourReference', e.target.value)}
                  className="form-input"
                />
              </div>
            </div>
          </section>

          {/* Shipping Information - Collapsible, optional */}
          <section className="form-section">
            <h2 
              className="section-title section-title-collapsible" 
              onClick={() => setIsShippingExpanded(!isShippingExpanded)}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <span style={{ fontSize: '0.75em', transition: 'transform 0.2s' }}>
                {isShippingExpanded ? '▼' : '▶'}
              </span>
              {t('workOrder.shippingOptional')}
            </h2>

            {isShippingExpanded && (
              <>
                <div className="form-row form-row-3">
                  <div className="form-group">
                    <label>{t('workOrder.billing.address')}</label>
                    <input
                      type="text"
                      value={formData.osoite}
                      onChange={(e) => handleChange('osoite', e.target.value)}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('workOrder.billing.postalCode')}</label>
                    <input
                      type="text"
                      value={formData.postinumero}
                      onChange={(e) => handleChange('postinumero', e.target.value)}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('workOrder.billing.city')}</label>
                    <input
                      type="text"
                      value={formData.paikkakunta}
                      onChange={(e) => handleChange('paikkakunta', e.target.value)}
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-row form-row-2">
                  <div className="form-group">
                    <label>{t('workOrder.billing.ovt')}</label>
                    <input
                      type="text"
                      value={formData.ovt}
                      onChange={(e) => handleChange('ovt', e.target.value)}
                      className="form-input"
                      placeholder="003712345678"
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('workOrder.billing.operator')}</label>
                    <input
                      type="text"
                      value={formData.operaattori}
                      onChange={(e) => handleChange('operaattori', e.target.value)}
                      className="form-input"
                    />
                  </div>
                </div>
              </>
            )}
          </section>

          {/* Site & Order Details */}
          <section className="form-section">
            <h2 className="section-title">{t('workOrder.order.title')} <span style={{ color: 'var(--color-error, #dc2626)' }}>*</span></h2>

            <div className="form-row form-row-2">
              <div className="form-group">
                <label>{t('workOrder.order.site')}</label>
                <input
                  type="text"
                  value={formData.kohde}
                  onChange={(e) => handleChange('kohde', e.target.value)}
                  className={`form-input ${fieldErrors.kohde ? 'input-error' : ''}`}
                />
                {fieldErrors.kohde && (
                  <span className="error-message">⚠️ {fieldErrors.kohde}</span>
                )}
              </div>

              <div className="form-group">
                <label>{t('workOrder.order.date')}</label>
                <input
                  type="date"
                  value={formData.ajankohta}
                  onChange={(e) => handleChange('ajankohta', e.target.value)}
                  className={`form-input ${fieldErrors.ajankohta ? 'input-error' : ''}`}
                />
                {fieldErrors.ajankohta && (
                  <span className="error-message">⚠️ {fieldErrors.ajankohta}</span>
                )}
              </div>
            </div>

            <div className="form-row form-row-3">
              <div className="form-group">
                <label>{t('workOrder.order.siteAddress')}</label>
                <input
                  type="text"
                  value={formData.kohdeOsoite}
                  onChange={(e) => handleChange('kohdeOsoite', e.target.value)}
                  className="form-input"
                  placeholder={t('workOrder.order.siteAddressPlaceholder')}
                />
              </div>

              <div className="form-group">
                <label>{t('workOrder.order.sitePostalCode')}</label>
                <input
                  type="text"
                  value={formData.kohdePostinumero}
                  onChange={(e) => handleChange('kohdePostinumero', e.target.value)}
                  className="form-input"
                  placeholder={t('workOrder.order.sitePostalCodePlaceholder')}
                />
              </div>

              <div className="form-group">
                <label>{t('workOrder.order.siteCity')}</label>
                <input
                  type="text"
                  value={formData.kohdePaikkakunta}
                  onChange={(e) => handleChange('kohdePaikkakunta', e.target.value)}
                  className="form-input"
                  placeholder={t('workOrder.order.siteCityPlaceholder')}
                />
              </div>
            </div>

            <div className="form-row form-row-2">
              <div className="form-group">
                <label>{t('workOrder.order.lineOfBusiness')}</label>
                <select
                  value={formData.lineOfBusiness}
                  onChange={(e) => handleChange('lineOfBusiness', e.target.value)}
                  className="form-input"
                >
                  <option value="">--</option>
                  <option value="ts">{t('workOrder.order.lineOfBusinessOptions.ts')}</option>
                  <option value="service">{t('workOrder.order.lineOfBusinessOptions.service')}</option>
                  <option value="fp">{t('workOrder.order.lineOfBusinessOptions.fp')}</option>
                  <option value="kec">{t('workOrder.order.lineOfBusinessOptions.kec')}</option>
                  <option value="rf">{t('workOrder.order.lineOfBusinessOptions.rf')}</option>
                  <option value="tc">{t('workOrder.order.lineOfBusinessOptions.tc')}</option>
                  <option value="install">{t('workOrder.order.lineOfBusinessOptions.install')}</option>
                  <option value="startup">{t('workOrder.order.lineOfBusinessOptions.startup')}</option>
                  <option value="spareParts">{t('workOrder.order.lineOfBusinessOptions.spareParts')}</option>
                  <option value="sparePartsIC">{t('workOrder.order.lineOfBusinessOptions.sparePartsIC')}</option>
                  <option value="on">{t('workOrder.order.lineOfBusinessOptions.on')}</option>
                  <option value="ww">{t('workOrder.order.lineOfBusinessOptions.ww')}</option>
                  <option value="maas">{t('workOrder.order.lineOfBusinessOptions.maas')}</option>
                </select>
              </div>

              <div className="form-group">
                <label>{t('workOrder.order.productGroup')}</label>
                <input
                  type="text"
                  value={formData.tuoteryhma}
                  onChange={(e) => handleChange('tuoteryhma', e.target.value)}
                  className="form-input"
                />
              </div>
            </div>
          </section>

          {/* Work Description - Optional */}
          <section className="form-section">
            <h2 className="section-title">{t('workOrder.description.title')}</h2>

            <div className="form-group">
              <label>{t('workOrder.order.description')}</label>
              <textarea
                value={formData.kuvaus}
                onChange={(e) => handleChange('kuvaus', e.target.value)}
                className="form-input"
                rows={3}
              />
              <small className="form-hint">{t('workOrder.order.descriptionHint')}</small>
            </div>

            <div className="form-group">
              <label>{t('workOrder.order.additionalInfo')}</label>
              <textarea
                value={formData.lisatietoja}
                onChange={(e) => handleChange('lisatietoja', e.target.value)}
                className="form-input"
                rows={2}
              />
              <small className="form-hint">{t('workOrder.order.additionalInfoHint')}</small>
            </div>
          </section>

          {/* Technician Section */}
          <section className="form-section">
            <h2 className="section-title">{t('workOrder.technician.title')}</h2>

            <div className="form-row form-row-2">
              <div className="form-group">
                <label>{t('workOrder.technician.name')}</label>
                <input
                  type="text"
                  value={formData.teknikonNimi}
                  onChange={(e) => handleChange('teknikonNimi', e.target.value)}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>{t('workOrder.technician.date')}</label>
                <input
                  type="date"
                  value={formData.paivamaara}
                  onChange={(e) => handleChange('paivamaara', e.target.value)}
                  className="form-input"
                />
              </div>
            </div>
          </section>

          {/* Billing Section */}
          <section className="form-section">
            <div className="section-header">
              <h2 className="section-title">{t('workOrder.invoiceLines.title')}</h2>
              {!isEditingPricing && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={handleTogglePricingEdit}
                >
                  {t('workOrder.billing.editPricing')}
                </button>
              )}
            </div>

            {linkedOfferId && (
              <p className="linked-quote-info">
                {t('workOrder.billing.linkedQuote')}: <strong>{linkedOfferId}</strong>
              </p>
            )}

            {/* Pricing Edit Mode Banner */}
            {isEditingPricing && (
              <div className="pricing-edit-banner">
                <span className="pricing-edit-icon">✏️</span>
                <span>{t('workOrder.editPricingBanner')}</span>
                <div className="pricing-edit-actions">
                  <button className="btn btn-sm btn-success" onClick={handleApplyPricingChanges}>
                    {t('workOrder.billing.applyChanges')}
                  </button>
                  <button className="btn btn-sm btn-secondary" onClick={handleCancelPricingEdit}>
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            )}

            {/* Inline Pricing Editor */}
            {isEditingPricing && (
              <div className="pricing-editor">
                {/* Custom Line Items */}
                <div className="pricing-section">
                  <div className="pricing-section-header">
                    <h3>{t('calculator.customLines.title')}</h3>
                    <button className="btn btn-secondary btn-sm" onClick={addCustomLine}>
                      {t('calculator.customLines.addButton')}
                    </button>
                  </div>
                  {localCustomLines.map((line, index) => (
                    <div key={line.id} className="item-card">
                      <div className="item-header">
                        <span className="item-label">#{index + 1}</span>
                        <button className="btn btn-danger btn-sm" onClick={() => removeCustomLine(line.id)}>
                          {t('calculator.customLines.removeButton')}
                        </button>
                      </div>
                      <div className="form-row form-row-3">
                        <div className="form-group">
                          <label>{t('calculator.customLines.name')}</label>
                          <input
                            type="text"
                            value={line.name}
                            onChange={(e) => updateCustomLine(line.id, 'name', e.target.value)}
                            placeholder={t('calculator.customLines.namePlaceholder')}
                            className="form-input"
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('calculator.customLines.description')}</label>
                          <input
                            type="text"
                            value={line.description}
                            onChange={(e) => updateCustomLine(line.id, 'description', e.target.value)}
                            placeholder={t('calculator.customLines.descriptionPlaceholder')}
                            className="form-input"
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('calculator.customLines.price')}</label>
                          <input
                            type="number"
                            value={line.price}
                            onChange={(e) => updateCustomLine(line.id, 'price', parseFloat(e.target.value) || 0)}
                            className="form-input"
                            min={0}
                            step={1}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Travel Items */}
                <div className="pricing-section">
                  <div className="pricing-section-header">
                    <h3>{t('calculator.travel.title')}</h3>
                    <button className="btn btn-secondary btn-sm" onClick={addTravelItem}>
                      {t('calculator.travel.addButton')}
                    </button>
                  </div>
                  {localTravelItems.map((item, index) => (
                    <div key={item.id} className="item-card">
                      <div className="item-header">
                        <span className="item-label">{t('calculator.travel.itemLabel')} #{index + 1}</span>
                        <button className="btn btn-danger btn-sm" onClick={() => removeTravelItem(item.id)}>
                          {t('calculator.travel.removeButton')}
                        </button>
                      </div>
                      <div className="form-row form-row-3">
                        <div className="form-group">
                          <label>{t('calculator.travel.mode')}</label>
                          <select
                            value={item.mode}
                            onChange={(e) => updateTravelItem(item.id, 'mode', e.target.value)}
                            className="form-input"
                          >
                            <option value="perKm">{t('calculator.travel.modePerKm')}</option>
                            <option value="fixed">{t('calculator.travel.modeFixed')}</option>
                          </select>
                        </div>
                        {item.mode === 'fixed' ? (
                          <div className="form-group">
                            <label>{t('calculator.travel.fixedPrice')}</label>
                            <input
                              type="number"
                              value={item.fixedPrice}
                              onChange={(e) => updateTravelItem(item.id, 'fixedPrice', parseFloat(e.target.value) || 0)}
                              className="form-input"
                              min={0}
                              step={10}
                            />
                          </div>
                        ) : (
                          <>
                            <div className="form-group">
                              <label>{t('calculator.travel.travelTime')}</label>
                              <input
                                type="number"
                                value={item.travelTime}
                                onChange={(e) => updateTravelItem(item.id, 'travelTime', parseFloat(e.target.value) || 0)}
                                className="form-input"
                                min={0}
                                step={0.5}
                              />
                            </div>
                            <div className="form-group">
                              <label>{t('calculator.travel.timeRate')}</label>
                              <input
                                type="number"
                                value={item.timeRate}
                                onChange={(e) => updateTravelItem(item.id, 'timeRate', parseFloat(e.target.value) || 0)}
                                className="form-input"
                                min={0}
                                step={5}
                              />
                            </div>
                          </>
                        )}
                      </div>
                      {item.mode === 'perKm' && (
                        <div className="form-row form-row-3">
                          <div className="form-group">
                            <label>{t('calculator.travel.distance')}</label>
                            <input
                              type="number"
                              value={item.distance}
                              onChange={(e) => updateTravelItem(item.id, 'distance', parseFloat(e.target.value) || 0)}
                              className="form-input"
                              min={0}
                              step={10}
                            />
                          </div>
                          <div className="form-group">
                            <label>{t('calculator.travel.distanceRate')}</label>
                            <input
                              type="number"
                              value={item.distanceRate}
                              onChange={(e) => updateTravelItem(item.id, 'distanceRate', parseFloat(e.target.value) || 0)}
                              className="form-input"
                              min={0}
                              step={0.05}
                            />
                          </div>
                          <div className="form-group">
                            <label>{t('calculator.travel.discount')}</label>
                            <div className="input-with-suffix">
                              <input
                                type="number"
                                value={item.discountPercent}
                                onChange={(e) => updateTravelItem(item.id, 'discountPercent', parseFloat(e.target.value) || 0)}
                                className="form-input"
                                min={0}
                                max={100}
                              />
                              <span className="input-suffix">%</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Work/Labor Items */}
                <div className="pricing-section">
                  <div className="pricing-section-header">
                    <h3>{t('calculator.labor.title')}</h3>
                    <button className="btn btn-secondary btn-sm" onClick={addWorkItem}>
                      {t('calculator.labor.addButton')}
                    </button>
                  </div>
                  {localWorkItems.map((item, index) => (
                    <div key={item.id} className="item-card">
                      <div className="item-header">
                        <span className="item-label">{t('calculator.labor.itemLabel')} #{index + 1}</span>
                        <button className="btn btn-danger btn-sm" onClick={() => removeWorkItem(item.id)}>
                          {t('calculator.labor.removeButton')}
                        </button>
                      </div>
                      <div className="form-row form-row-3">
                        <div className="form-group">
                          <label>{t('calculator.labor.technicians')}</label>
                          <input
                            type="number"
                            value={item.technicians}
                            onChange={(e) => updateWorkItem(item.id, 'technicians', parseInt(e.target.value) || 1)}
                            className="form-input"
                            min={1}
                            max={10}
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('calculator.labor.hours')}</label>
                          <input
                            type="number"
                            value={item.hours}
                            onChange={(e) => updateWorkItem(item.id, 'hours', parseFloat(e.target.value) || 0)}
                            className="form-input"
                            min={0}
                            step={0.5}
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('calculator.labor.hourlyRate')}</label>
                          <input
                            type="number"
                            value={item.hourlyRate}
                            onChange={(e) => updateWorkItem(item.id, 'hourlyRate', parseFloat(e.target.value) || 0)}
                            className="form-input"
                            min={0}
                            step={5}
                          />
                        </div>
                      </div>
                      <div className="form-row form-row-2">
                        <div className="form-group">
                          <label>{t('calculator.labor.callOutFee')}</label>
                          <select
                            value={item.callOutFee}
                            onChange={(e) => updateWorkItem(item.id, 'callOutFee', parseInt(e.target.value))}
                            className="form-input"
                          >
                            <option value={0}>No call-out fee</option>
                            <option value={50}>€50</option>
                            <option value={75}>€75</option>
                            <option value={100}>€100</option>
                            <option value={150}>€150</option>
                            <option value={-1}>Custom</option>
                          </select>
                        </div>
                        {item.callOutFee === -1 && (
                          <div className="form-group">
                            <label>{t('calculator.labor.customCallOutFee')}</label>
                            <input
                              type="number"
                              value={item.customCallOutFee}
                              onChange={(e) => updateWorkItem(item.id, 'customCallOutFee', parseFloat(e.target.value) || 0)}
                              className="form-input"
                              min={0}
                            />
                          </div>
                        )}
                      </div>
                      <div className="form-group">
                        <label>{t('calculator.labor.surcharges')}</label>
                        <div className="checkbox-group">
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={item.nightSunday}
                              onChange={(e) => updateWorkItem(item.id, 'nightSunday', e.target.checked)}
                            />
                            <span>{t('calculator.labor.nightSunday')}</span>
                          </label>
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={item.urgent}
                              onChange={(e) => updateWorkItem(item.id, 'urgent', e.target.checked)}
                            />
                            <span>{t('calculator.labor.urgent')}</span>
                          </label>
                        </div>
                      </div>
                      <div className="form-row form-row-2">
                        <div className="form-group">
                          <label>{t('calculator.labor.accommodation')}</label>
                          <input
                            type="number"
                            value={item.accommodationNights}
                            onChange={(e) => updateWorkItem(item.id, 'accommodationNights', parseInt(e.target.value) || 0)}
                            className="form-input"
                            min={0}
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('calculator.labor.perDiem')}</label>
                          <input
                            type="number"
                            value={item.perDiemDays}
                            onChange={(e) => updateWorkItem(item.id, 'perDiemDays', parseInt(e.target.value) || 0)}
                            className="form-input"
                            min={0}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Spare Parts */}
                <div className="pricing-section">
                  <div className="pricing-section-header">
                    <h3>{t('calculator.parts.title')}</h3>
                    <button className="btn btn-secondary btn-sm" onClick={addPart}>
                      {t('calculator.parts.addButton')}
                    </button>
                  </div>
                  {localParts.map((part, index) => (
                    <div key={part.id} className="item-card">
                      <div className="item-header">
                        <span className="item-label">#{index + 1}</span>
                        <button className="btn btn-danger btn-sm" onClick={() => removePart(part.id)}>
                          {t('calculator.parts.removeButton')}
                        </button>
                      </div>
                      <div className="form-row form-row-4">
                        <div className="form-group">
                          <label>{t('calculator.parts.description')}</label>
                          <input
                            type="text"
                            value={part.description}
                            onChange={(e) => updatePart(part.id, 'description', e.target.value)}
                            placeholder={t('calculator.parts.descriptionPlaceholder')}
                            className="form-input"
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('calculator.parts.quantity')}</label>
                          <input
                            type="number"
                            value={part.quantity}
                            onChange={(e) => updatePart(part.id, 'quantity', parseInt(e.target.value) || 1)}
                            className="form-input"
                            min={1}
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('calculator.parts.unitPrice')}</label>
                          <input
                            type="number"
                            value={part.unitPrice}
                            onChange={(e) => updatePart(part.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                            className="form-input"
                            min={0}
                            step={0.01}
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('calculator.parts.markup')}</label>
                          <input
                            type="number"
                            value={part.markup}
                            onChange={(e) => updatePart(part.id, 'markup', parseFloat(e.target.value) || 0)}
                            className="form-input"
                            min={0}
                            max={100}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Show summary when not editing */}
            {!isEditingPricing && (calculatedTotals.subtotal > 0) && (
              <div className="pricing-summary-compact">
                {calculatedTotals.breakdown.customLines > 0 && (
                  <div className="pricing-summary-row">
                    <span>{t('calculator.summary.customLines')}</span>
                    <span>{formatCurrency(calculatedTotals.breakdown.customLines, settings.companyCountry)}</span>
                  </div>
                )}
                {calculatedTotals.breakdown.travel > 0 && (
                  <div className="pricing-summary-row">
                    <span>{t('calculator.summary.travel')}</span>
                    <span>{formatCurrency(calculatedTotals.breakdown.travel, settings.companyCountry)}</span>
                  </div>
                )}
                {calculatedTotals.breakdown.labor > 0 && (
                  <div className="pricing-summary-row">
                    <span>{t('calculator.summary.labor')}</span>
                    <span>{formatCurrency(calculatedTotals.breakdown.labor, settings.companyCountry)}</span>
                  </div>
                )}
                {calculatedTotals.breakdown.callOutFee > 0 && (
                  <div className="pricing-summary-row">
                    <span>{t('calculator.summary.callOutFee')}</span>
                    <span>{formatCurrency(calculatedTotals.breakdown.callOutFee, settings.companyCountry)}</span>
                  </div>
                )}
                {calculatedTotals.breakdown.accommodation > 0 && (
                  <div className="pricing-summary-row">
                    <span>{t('calculator.summary.accommodation')}</span>
                    <span>{formatCurrency(calculatedTotals.breakdown.accommodation, settings.companyCountry)}</span>
                  </div>
                )}
                {calculatedTotals.breakdown.parts > 0 && (
                  <div className="pricing-summary-row">
                    <span>{t('calculator.summary.parts')}</span>
                    <span>{formatCurrency(calculatedTotals.breakdown.parts, settings.companyCountry)}</span>
                  </div>
                )}
              </div>
            )}

            {!isEditingPricing && calculatedTotals.subtotal === 0 && (
              <p className="no-pricing-info">{t('workOrder.billing.noPricingItems')}</p>
            )}
          </section>
        </div>

        {/* Right Column - Summary */}
        <div className="calculator-summary">
          <div className="summary-card sticky">
            <h2 className="section-title">{t('calculator.summary.title')}</h2>

            <div className="summary-rows">
              {calculatedTotals.breakdown.customLines > 0 && (
                <div className="summary-row">
                  <span>{t('calculator.summary.customLines')}</span>
                  <span>{formatCurrency(calculatedTotals.breakdown.customLines, settings.companyCountry)}</span>
                </div>
              )}
              {calculatedTotals.breakdown.travel > 0 && (
                <div className="summary-row">
                  <span>{t('calculator.summary.travel')}</span>
                  <span>{formatCurrency(calculatedTotals.breakdown.travel, settings.companyCountry)}</span>
                </div>
              )}
              {calculatedTotals.breakdown.labor > 0 && (
                <div className="summary-row">
                  <span>{t('calculator.summary.labor')}</span>
                  <span>{formatCurrency(calculatedTotals.breakdown.labor, settings.companyCountry)}</span>
                </div>
              )}
              {calculatedTotals.breakdown.callOutFee > 0 && (
                <div className="summary-row">
                  <span>{t('calculator.summary.callOutFee')}</span>
                  <span>{formatCurrency(calculatedTotals.breakdown.callOutFee, settings.companyCountry)}</span>
                </div>
              )}
              {calculatedTotals.breakdown.accommodation > 0 && (
                <div className="summary-row">
                  <span>{t('calculator.summary.accommodation')}</span>
                  <span>{formatCurrency(calculatedTotals.breakdown.accommodation, settings.companyCountry)}</span>
                </div>
              )}
              {calculatedTotals.breakdown.parts > 0 && (
                <div className="summary-row">
                  <span>{t('calculator.summary.parts')}</span>
                  <span>{formatCurrency(calculatedTotals.breakdown.parts, settings.companyCountry)}</span>
                </div>
              )}
            </div>

            <hr />

            <div className="summary-row">
              <span>{t('calculator.summary.subtotal')}</span>
              <span className="summary-value">{formatCurrency(calculatedTotals.subtotal, settings.companyCountry)}</span>
            </div>

            <div className="summary-row">
              <span>{t('calculator.summary.vat')} ({calculatedTotals.vatPercent}%)</span>
              <span>{formatCurrency(calculatedTotals.vat, settings.companyCountry)}</span>
            </div>

            <div className="summary-row total">
              <span>{t('calculator.summary.total')}</span>
              <span className="summary-total">{formatCurrency(calculatedTotals.total, settings.companyCountry)}</span>
            </div>

            <div className="summary-actions">
              <button className="btn btn-secondary" onClick={handleClear}>
                {t('workOrder.actions.clearForm')}
              </button>
              {onPrint && editingWorkOrderId && (
                <button
                  className="btn btn-teal"
                  onClick={handlePrint}
                >
                  🖨️ {t('workOrder.actions.print')}
                </button>
              )}
              <button className="btn btn-primary" onClick={handleSave}>
                {editingWorkOrderId ? t('workOrder.actions.updateWorkOrder') : t('workOrder.actions.saveWorkOrder')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkOrderForm;

